import { useEffect, useMemo, useState } from 'react';

import { GetLocationsRequestDTO, GetLocationsResponseDTO, LocationColumnId, MODULE, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import ArchiveIcon from '@/components/icons/archive';
import DownloadIcon from '@/components/icons/download';
import FolderOpenIcon from '@/components/icons/folder-open';
import PlusIcon from '@/components/icons/plus';
import UndoIcon from '@/components/icons/undo';
import BulkLocationImportDialog from '@/components/locations/bulk-location-import-dialog';
import LocationDrawer from '@/components/locations/location-drawer';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { getLocationsInfiniteQueryOptions, locationKeys } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';

import { LocationsTable, NoLocationsCreatedDisplay, NoLocationsFoundFromFiltersDisplay, NoLocationsFoundFromSearchDisplay } from './table';
import { createLocationsTableColumns } from './table-meta';

function LocationsPage() {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const { canManageLocations, canExportCsv } = usePermissions();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.Locations, {
    columns: createLocationsTableColumns({ canManageLocations }),
  });

  const [openLocationDrawer, setOpenLocationDrawer] = useState(false);
  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [locationState, setLocationState] = useState('active');

  const {
    data: locationsData,
    fetchNextPage: fetchNextLocationsPage,
    hasNextPage: hasNextLocationsPage,
    isFetching: isFetchingLocations,
    isFetchingNextPage: isFetchingNextLocationsPage,
    error: locationsError,
  } = useInfiniteQuery(
    getLocationsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        state: locationState as RecordState,
      },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetLocationsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  function handleCsvExport() {
    openExportTab({
      kind: 'csv',
      request: {
        queryType: 'locations',
        filename: buildCsvExportFilename('locations'),
        filters: {
          state: locationState as RecordState,
          fields: { id: selectedLocations.map((location) => location.id) },
        },
        orderBy: sorting.key
          ? {
              field: sorting.key as NonNullable<GetLocationsRequestDTO['orderBy']>['field'],
              direction: sorting.direction as 'asc' | 'desc',
            }
          : undefined,
      },
    });
  }

  const { mutateAsync: bulkUpdateLocationState, isPending: isUpdatingLocationState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState.Active | RecordState.Archived }) =>
      await ApiService.BulkUpdateLocationState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Locations restored successfully',
        [RecordState.Archived]: 'Locations archived successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenRestoreConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: locationKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleArchiveLocations() {
    if (locationsData?.pages) {
      const toArchive = getSelectedLocations(locationsData);
      bulkUpdateLocationState({ ids: toArchive.map((location) => location.id), state: RecordState.Archived });
    }
  }

  function handleRestoreLocations() {
    if (locationsData?.pages) {
      const toRestore = getSelectedLocations(locationsData);
      bulkUpdateLocationState({ ids: toRestore.map((location) => location.id), state: RecordState.Active });
    }
  }

  const getSelectedLocations = useMemo(() => {
    return (incomingData: typeof locationsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const locations = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => locations[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedLocations = useMemo(() => getSelectedLocations(locationsData), [locationsData, getSelectedLocations]);

  useEffect(() => {
    if (locationsError) {
      toast.error(locationsError.message);
    }
  }, [toast, locationsError]);

  useResetRowSelectionOnChange(setSelectedRows, [locationState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextLocationsPage,
    hasNextPage: hasNextLocationsPage,
    isFetching: isFetchingLocations,
    isError: !!locationsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => locationsData?.pages.flatMap((page) => page.data) ?? [], [locationsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedLocations.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Locations</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: 'active' },
                      { icon: <ArchiveIcon />, label: 'Archived', value: 'archived' },
                    ]}
                    value={locationState}
                    onChange={setLocationState}
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.Locations, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <ColumnsDialog<GetLocationsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={createLocationsTableColumns({ canManageLocations })}
                      defaultSectionLabel="Location"
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: LocationColumnId; active: boolean }> },
                        })
                      }
                    />
                  </div>
                  {canManageLocations && <BulkLocationImportDialog />}
                  {canManageLocations && (
                    <TablePageHeaderActionButton
                      variant="success"
                      icon={<PlusIcon />}
                      kind="primary"
                      onClick={() => setOpenLocationDrawer(true)}
                      label="Create Location"
                    />
                  )}
                  {canManageLocations && <LocationDrawer noTrigger open={openLocationDrawer} onOpenChange={setOpenLocationDrawer} />}
                </div>
              </>
            )}

            {selectedLocations.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedLocations.length} totalCount={locationsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadIcon />} label="Download CSV" onClick={handleCsvExport} />}
                {canManageLocations && locationState === 'active' && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {canManageLocations && locationState === 'archived' && (
                  <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <LocationsTable
          data={flattenedData}
          loading={(isFetchingLocations && !isFetchingNextLocationsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoLocationsFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoLocationsFoundFromFiltersDisplay
                openLocationDrawer={() => setOpenLocationDrawer(true)}
                removeColumnFilters={() => setModuleFilters(MODULE.Locations, [])}
              />
            ) : (
              <NoLocationsCreatedDisplay openLocationDrawer={() => setOpenLocationDrawer(true)} />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.Locations, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextLocationsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextLocationsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveLocations}
        loading={isUpdatingLocationState}
        title="Archive locations?"
        description="Are you sure you want to archive these locations? You can restore them later from the archived tab."
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreLocations}
        loading={isUpdatingLocationState}
        title="Restore locations?"
        description="Are you sure you want to restore these locations? They will be moved back to your active locations."
      />
    </div>
  );
}

export default LocationsPage;
